'use client';
import React, { useState, useMemo } from 'react';
import { Search, Filter, Eye, Phone, Mail, CheckCheck, XCircle, ChevronUp, ChevronDown } from 'lucide-react';
import LeverageBadge from '@/components/ui/LeverageBadge';
import StatusBadge from '@/components/ui/StatusBadge';
import AppImage from '@/components/ui/AppImage';
import { Lead, LeverageScore, LeadStatus } from './mockLeads';

type SortField = 'submittedAt' | 'estimateMax' | 'leverageScore' | 'status';
type SortDir = 'asc' | 'desc';

const scoreOrder: Record<LeverageScore, number> = { High: 3, Medium: 2, Low: 1 };
const statusOrder: Record<LeadStatus, number> = { New: 5, Reviewed: 4, Contacted: 3, Won: 2, Rejected: 1 };

interface Props {
  leads: Lead[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onSelectAll: (checked: boolean) => void;
  onStatusChange: (id: string, status: LeadStatus) => void;
  onViewLead: (lead: Lead) => void;
  onBulkAction: (action: 'Contacted' | 'Rejected') => void;
}

export default function LeadInboxTable({
  leads, selectedIds, onToggleSelect, onSelectAll, onStatusChange, onViewLead, onBulkAction,
}: Props) {
  const [search, setSearch] = useState('');
  const [filterScore, setFilterScore] = useState<LeverageScore | 'All'>('All');
  const [filterStatus, setFilterStatus] = useState<LeadStatus | 'All'>('All');
  const [sortField, setSortField] = useState<SortField>('submittedAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [openStatusDropdown, setOpenStatusDropdown] = useState<string | null>(null);

  const filtered = useMemo(() => {
    let result = leads;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (l) =>
          `${l.firstName} ${l.lastName}`.toLowerCase().includes(q) ||
          l.projectType.toLowerCase().includes(q) ||
          l.email.toLowerCase().includes(q) ||
          l.address.toLowerCase().includes(q)
      );
    }
    if (filterScore !== 'All') result = result.filter((l) => l.leverageScore === filterScore);
    if (filterStatus !== 'All') result = result.filter((l) => l.status === filterStatus);

    result = [...result].sort((a, b) => {
      let av: number, bv: number;
      if (sortField === 'submittedAt') {
        av = new Date(a.submittedAt).getTime();
        bv = new Date(b.submittedAt).getTime();
      } else if (sortField === 'estimateMax') {
        av = a.estimateMax;
        bv = b.estimateMax;
      } else if (sortField === 'leverageScore') {
        av = scoreOrder[a.leverageScore];
        bv = scoreOrder[b.leverageScore];
      } else {
        av = statusOrder[a.status];
        bv = statusOrder[b.status];
      }
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return result;
  }, [leads, search, filterScore, filterStatus, sortField, sortDir]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronUp size={12} className="text-[#444]" />;
    return sortDir === 'asc' ? <ChevronUp size={12} className="text-blue-400" /> : <ChevronDown size={12} className="text-blue-400" />;
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const statusOptions: LeadStatus[] = ['New', 'Reviewed', 'Contacted', 'Won', 'Rejected'];

  return (
    <div className="leverage-card overflow-hidden">
      {/* Table header controls */}
      <div className="p-4 border-b border-[#1F1F1F] flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 min-w-0">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search leads by name, project, or address..."
            className="leverage-input pl-9 py-2.5 text-sm"
          />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Filter size={14} className="text-[#555]" />
          <select
            value={filterScore}
            onChange={(e) => { setFilterScore(e.target.value as LeverageScore | 'All'); setPage(1); }}
            className="bg-[#0D0D0D] border border-[#2A2A2A] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-600"
          >
            <option value="All">All Scores</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value as LeadStatus | 'All'); setPage(1); }}
            className="bg-[#0D0D0D] border border-[#2A2A2A] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-600"
          >
            <option value="All">All Statuses</option>
            <option value="New">New</option>
            <option value="Reviewed">Reviewed</option>
            <option value="Contacted">Contacted</option>
            <option value="Won">Won</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="px-4 py-3 bg-blue-600/10 border-b border-blue-600/20 flex items-center justify-between animate-slide-up">
          <span className="text-blue-300 text-sm font-600">{selectedIds.size} lead{selectedIds.size > 1 ? 's' : ''} selected</span>
          <div className="flex gap-2">
            <button
              onClick={() => onBulkAction('Contacted')}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600/20 border border-blue-600/30 rounded-lg text-blue-300 text-xs font-600 hover:bg-blue-600/30 transition-colors"
            >
              <CheckCheck size={13} />
              Mark Contacted
            </button>
            <button
              onClick={() => onBulkAction('Rejected')}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-xs font-600 hover:bg-red-500/20 transition-colors"
            >
              <XCircle size={13} />
              Reject All
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="border-b border-[#1F1F1F]">
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={selectedIds.size === leads.length && leads.length > 0}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  className="rounded border-[#3A3A3A] bg-[#0D0D0D] accent-blue-600"
                />
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-600 text-[#555] uppercase tracking-wider">Lead</th>
              <th className="px-4 py-3 text-left text-[11px] font-600 text-[#555] uppercase tracking-wider">Photo</th>
              <th className="px-4 py-3 text-left text-[11px] font-600 text-[#555] uppercase tracking-wider">Project</th>
              <th
                className="px-4 py-3 text-left text-[11px] font-600 text-[#555] uppercase tracking-wider cursor-pointer hover:text-white transition-colors select-none"
                onClick={() => handleSort('leverageScore')}
              >
                <div className="flex items-center gap-1">Score <SortIcon field="leverageScore" /></div>
              </th>
              <th
                className="px-4 py-3 text-left text-[11px] font-600 text-[#555] uppercase tracking-wider cursor-pointer hover:text-white transition-colors select-none"
                onClick={() => handleSort('estimateMax')}
              >
                <div className="flex items-center gap-1">Estimate <SortIcon field="estimateMax" /></div>
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-600 text-[#555] uppercase tracking-wider">Budget</th>
              <th
                className="px-4 py-3 text-left text-[11px] font-600 text-[#555] uppercase tracking-wider cursor-pointer hover:text-white transition-colors select-none"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center gap-1">Status <SortIcon field="status" /></div>
              </th>
              <th
                className="px-4 py-3 text-left text-[11px] font-600 text-[#555] uppercase tracking-wider cursor-pointer hover:text-white transition-colors select-none"
                onClick={() => handleSort('submittedAt')}
              >
                <div className="flex items-center gap-1">Submitted <SortIcon field="submittedAt" /></div>
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-600 text-[#555] uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#1A1A1A] flex items-center justify-center">
                      <Search size={22} className="text-[#444]" />
                    </div>
                    <p className="text-white font-600">No leads match your filters</p>
                    <p className="text-[#555] text-sm">Try adjusting your search or filter criteria</p>
                  </div>
                </td>
              </tr>
            ) : (
              paginated.map((lead) => (
                <tr
                  key={lead.id}
                  className={`border-b border-[#111] hover:bg-[#141414] transition-colors group ${selectedIds.has(lead.id) ? 'bg-blue-600/5' : ''}`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(lead.id)}
                      onChange={() => onToggleSelect(lead.id)}
                      className="rounded border-[#3A3A3A] bg-[#0D0D0D] accent-blue-600"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-white font-600 text-sm">{lead.firstName} {lead.lastName}</p>
                      <p className="text-[#555] text-xs mt-0.5 truncate max-w-[140px]">{lead.email}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => onViewLead(lead)}
                      className="relative w-12 h-10 rounded-lg overflow-hidden border border-[#2A2A2A] hover:border-blue-500/50 transition-colors group/img"
                    >
                      <AppImage
                        src={lead.photoUrl}
                        alt={lead.photoAlt}
                        fill
                        className="object-cover"
                        sizes="48px"
                      />
                      <div className="absolute inset-0 bg-blue-600/0 group-hover/img:bg-blue-600/20 transition-colors flex items-center justify-center">
                        <Eye size={12} className="text-white opacity-0 group-hover/img:opacity-100 transition-opacity" />
                      </div>
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-[#CCC] text-sm truncate max-w-[130px]">{lead.projectType}</p>
                    <p className="text-[#555] text-xs mt-0.5">{lead.timeline}</p>
                  </td>
                  <td className="px-4 py-3">
                    <LeverageBadge level={lead.leverageScore} size="sm" />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-white font-700 tabular-nums text-sm">
                      ${lead.estimateMin.toLocaleString()}–${lead.estimateMax.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[#888] text-xs">{lead.budgetRange}</span>
                  </td>
                  <td className="px-4 py-3 relative">
                    <button
                      onClick={() => setOpenStatusDropdown(openStatusDropdown === lead.id ? null : lead.id)}
                      className="hover:opacity-80 transition-opacity"
                    >
                      <StatusBadge status={lead.status} />
                    </button>
                    {openStatusDropdown === lead.id && (
                      <div className="absolute z-30 top-full left-0 mt-1 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg shadow-modal overflow-hidden min-w-[130px]">
                        {statusOptions.map((s) => (
                          <button
                            key={`status-opt-${s}`}
                            onClick={() => { onStatusChange(lead.id, s); setOpenStatusDropdown(null); }}
                            className={`w-full text-left px-3 py-2 text-xs font-500 hover:bg-[#2A2A2A] transition-colors ${lead.status === s ? 'text-blue-400' : 'text-[#AAA]'}`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[#555] text-xs tabular-nums">{formatDate(lead.submittedAt)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => onViewLead(lead)}
                        title="View lead details"
                        className="w-7 h-7 rounded-lg bg-[#1A1A1A] hover:bg-blue-600/20 hover:text-blue-400 text-[#666] flex items-center justify-center transition-colors"
                      >
                        <Eye size={13} />
                      </button>
                      <a
                        href={`tel:${lead.phone}`}
                        title={`Call ${lead.firstName}`}
                        className="w-7 h-7 rounded-lg bg-[#1A1A1A] hover:bg-emerald-600/20 hover:text-emerald-400 text-[#666] flex items-center justify-center transition-colors"
                      >
                        <Phone size={13} />
                      </a>
                      <a
                        href={`mailto:${lead.email}`}
                        title={`Email ${lead.firstName}`}
                        className="w-7 h-7 rounded-lg bg-[#1A1A1A] hover:bg-amber-500/20 hover:text-amber-400 text-[#666] flex items-center justify-center transition-colors"
                      >
                        <Mail size={13} />
                      </a>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-4 py-3 border-t border-[#1F1F1F] flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#555]">Show</span>
          <select
            value={perPage}
            onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
            className="bg-[#0D0D0D] border border-[#2A2A2A] rounded px-2 py-1 text-xs text-white focus:outline-none"
          >
            {[5, 10, 25].map((n) => <option key={`per-page-${n}`} value={n}>{n}</option>)}
          </select>
          <span className="text-xs text-[#555]">of {filtered.length} leads</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 rounded-lg text-xs font-500 border border-[#2A2A2A] text-[#666] hover:text-white hover:border-[#3A3A3A] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            ← Prev
          </button>
          {Array.from({ length: totalPages }).map((_, i) => {
            const p = i + 1;
            return (
              <button
                key={`page-${p}`}
                onClick={() => setPage(p)}
                className={`w-8 h-8 rounded-lg text-xs font-600 transition-colors ${page === p ? 'bg-blue-600 text-white' : 'text-[#666] hover:text-white hover:bg-[#1A1A1A]'}`}
              >
                {p}
              </button>
            );
          })}
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1.5 rounded-lg text-xs font-500 border border-[#2A2A2A] text-[#666] hover:text-white hover:border-[#3A3A3A] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}